import {
  AVATAR_REGION_PATHS,
  AVATAR_VIEWBOX,
  BODY_REGION_ORDER,
  REGION_TO_METRIC,
  type BodyRegionKey,
} from '@/data/avatarZones';
import type { Audience, FitZone, FitZoneState } from '@/types/domain';

// Exported so tests can assert against the same source of truth rather than
// duplicating hex values — see tests/components/AvatarMaskOverlay.test.tsx.
export const ZONE_COLOR: Record<Exclude<FitZone, 'inactive'>, string> = {
  tight: '#F34C5E',
  neutral: '#82C64A',
  loose: '#F5CB55',
};

const ZONE_LABEL: Record<Exclude<FitZone, 'inactive'>, string> = {
  tight: 'tighter fit',
  neutral: 'comfort / neutral fit',
  loose: 'looser fit',
};

const REGION_HUMAN_NAME: Record<BodyRegionKey, string> = {
  Shoulders: 'shoulders',
  Chest: 'chest/bust',
  ArmL: 'left upper arm',
  ArmR: 'right upper arm',
  Waist: 'waist',
  Hip: 'hip/seat',
  ThighL: 'left thigh',
  ThighR: 'right thigh',
};

interface Props {
  audience: Audience;
  zones: FitZoneState;
}

/**
 * Renders translucent, silhouette-aligned fit shading over the avatar.
 * Regions whose metric is 'inactive' (not relevant to the selected garment
 * category, or not part of this comparison) render nothing — never a
 * default/neutral fill — so the shading only ever shows real information.
 */
export function AvatarMaskOverlay({ audience, zones }: Props) {
  const paths = AVATAR_REGION_PATHS[audience];

  return (
    <svg
      viewBox={AVATAR_VIEWBOX}
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden={false}
      role="img"
      aria-label={buildAriaSummary(zones)}
    >
      {BODY_REGION_ORDER.map((region) => {
        const metric = REGION_TO_METRIC[region];
        const zone = zones[metric];
        if (zone === 'inactive') return null;
        return (
          <path
            key={region}
            d={paths[region]}
            fill={ZONE_COLOR[zone]}
            fillOpacity={0.62}
            style={{ filter: 'drop-shadow(0 1px 1.5px rgba(20,20,20,0.12))' }}
            className="motion-safe:transition-[fill-opacity] motion-safe:duration-300"
          >
            <title>
              {REGION_HUMAN_NAME[region]}: {ZONE_LABEL[zone]}
            </title>
          </path>
        );
      })}
    </svg>
  );
}

function buildAriaSummary(zones: FitZoneState): string {
  const parts: string[] = [];
  (['chest', 'waist', 'hip'] as const).forEach((metric) => {
    const zone = zones[metric];
    if (zone === 'inactive') return;
    parts.push(`${metric}: ${ZONE_LABEL[zone]}`);
  });
  return parts.length > 0
    ? `Fit shading — ${parts.join(', ')}`
    : 'No fit shading active for this comparison';
}
